import React from 'react'
import PropTypes from 'prop-types'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { GAP_REASON_MESSAGE_IDS, GAP_REASONS, LICENSE_LABEL_MESSAGE_IDS } from '../../data/constants'
import { useAIRiskReportExportMode } from '../RenderMode/AIRiskReportRenderModeProvider'
import styles from './GapIcon.module.scss'

// Shown where a subsection's % would go when it has no score, explaining the grey state on hover:
// coming soon, requires an extra license, or simply not assessed for this tenant.
const GAP_ICON_BY_REASON = {
  [GAP_REASONS.COMING_SOON]: 'fa-regular fa-clock',
  [GAP_REASONS.LICENSE_REQUIRED]: 'fa-solid fa-lock',
  [GAP_REASONS.NOT_ASSESSED]: 'fa-regular fa-circle-question',
}

const getMessageId = ({ gap, licenseLabelId }) => {
  if (gap.isPartial && gap.reason === GAP_REASONS.LICENSE_REQUIRED) {
    return licenseLabelId && gap.capabilityLabelId
      ? 'AI_RISK_REPORT.GAP.PARTIAL_LICENSE_REQUIRED_NAMED'
      : 'AI_RISK_REPORT.GAP.PARTIAL_LICENSE_REQUIRED'
  }
  if (licenseLabelId) return 'AI_RISK_REPORT.GAP.LICENSE_REQUIRED_NAMED'
  return GAP_REASON_MESSAGE_IDS[gap.reason] ?? GAP_REASON_MESSAGE_IDS[GAP_REASONS.NOT_ASSESSED]
}

export default function GapIcon({ gap, id }) {
  const isExportMode = useAIRiskReportExportMode()
  const intl = useIntl()

  if (!gap) return null

  // Name the specific license when the gap is license-gated and we know which one.
  const licenseLabelId = gap.reason === GAP_REASONS.LICENSE_REQUIRED && LICENSE_LABEL_MESSAGE_IDS[gap.requiredLicense]
  const messageId = getMessageId({ gap, licenseLabelId })
  const values = {
    capability: gap.capabilityLabelId ? intl.formatMessage({ id: gap.capabilityLabelId }) : undefined,
    license: licenseLabelId ? intl.formatMessage({ id: licenseLabelId }) : undefined,
  }
  const iconClass = `${GAP_ICON_BY_REASON[gap.reason] ?? GAP_ICON_BY_REASON[GAP_REASONS.NOT_ASSESSED]} ${styles.icon}`

  if (isExportMode) {
    return <i className={iconClass} aria-hidden="true" />
  }

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={id}>
          <FormattedMessage id={messageId} values={values} />
        </Tooltip>
      }
    >
      <button type="button" className={styles.trigger} aria-label={intl.formatMessage({ id: messageId }, values)}>
        <i className={iconClass} aria-hidden="true" />
      </button>
    </OverlayTrigger>
  )
}

GapIcon.propTypes = {
  gap: PropTypes.shape({
    reason: PropTypes.string,
    requiredLicense: PropTypes.string,
    capabilityLabelId: PropTypes.string,
    isPartial: PropTypes.bool,
  }),
  id: PropTypes.string.isRequired,
}
